import { Button, Card, Col, FloatingLabel, FormCheck, FormControl, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle, Row, Spinner } from "react-bootstrap";
import PrivateDocDownloadListItem from "./PrivateDocDownloadListItem";
import { useState } from "react";
import PrivateApi from "../../../services/PrivateApiService";
import { FaRegFileZipper } from "react-icons/fa6";
import { useQueryClient } from "react-query";
import PdfModal from "../../shared/PdfModal";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useAuthState } from "../../../context/AuthContext";

/**
 * PrivateDocDownloadList component displays a list of invoices for download.
 * 
 * It allows users to select invoices that have not been downloaded yet, download them in a zip file, and ask questions about specific invoices by creating support tickets.
 * It also handles the display of previously downloaded invoices.
 * This component uses the PrivateApi service to interact with the backend and the react-query library to manage data fetching and caching.
 * It provides a user-friendly interface for managing private documents related to invoices, including options to select all not downloaded invoices, download selected invoices, and create support tickets for inquiries about specific invoices.
 * It also includes a loading state while documents are being prepared for download and a download state once the documents are ready.
 * 
 * @param {Array} notDownloadedInvoices - List of invoices that have not been downloaded yet
 * @param {Array} oldInvoices - List of previously downloaded invoices 
 * @returns Component that displays a list of invoices for download
 */
const PrivateDocDownloadList = ({ notDownloadedInvoices, oldInvoices }) => {

    const API = new PrivateApi(); // Initialize the API service
    const { user } = useAuthState(); // Get the authenticated user from the context
    const queryClient = useQueryClient(); // Get the query client for managing data fetching and caching
    const [view, setView] = useState("list"); // State to control the view type
    const [modalItem, setModalItem] = useState(null); // State to hold the item for which a question modal is opened
    const [downloadList, setDownloadList] = useState([]); // State to hold the list of selected invoices for download
    const [fileList, setFileList] = useState([]); // State to hold the list of files ready for download
    const [questionModal, setQuestionModal] = useState(false); // State to control the visibility of the question modal
    const [questionModalComment, setQuestionModalComment] = useState(""); // State to hold the comment for the question modal
    const [pdfModal, setPdfModal] = useState(false); // State to controll the visibility of the question modal
    const [pdfPreviewFile, setPdfPreviewFile] = useState("");
    const [modalView, setModalView] = useState("form"); // State to control the view of the modal
    const [questionCommentError, setQuestionCommentError] = useState(false);

    // Function to open the question modal with the selected item
    const openQuestionModal = (item, other_type=null) => {
        setModalItem({item: item, other_type: other_type});
        setModalView("form");
        setQuestionModal(true);
    }

    const openPreview = (file) => {
        setPdfPreviewFile(file);
        setPdfModal(true);
    }

    // Function to download a zip file from a base64 encoded string
    const downloadZip = (file) => {
        const base64 = file.file.split(',').pop();
        
        // construct a Blob from the base64 string
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Create a blob from the bytes
        const blob = new Blob([bytes], { type: "application/zip" });

        // Create a download link and trigger it
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = file.name;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    // Function to handle adding or removing items from the download list
    const downlaodListHandler = (type, id, opt, action) => {
        if (action === "push") {
            setDownloadList((prevList) => [...prevList, {type: type, id: id, option: opt}]);
        
        } else {
            setDownloadList((prevList) => prevList.filter(item => item.type !== type || item.id !== id || item.option !== opt));
        }    
    }

    // Function to handle the download button click
    // It prepares the selected invoices for download and updates the view accordingly
    // If there are no selected invoices, it alerts the user to select at least one invoice
    // It also handles the API call to download the invoices and updates the file list with the downloaded files
    // After downloading, it resets the download list and invalidates the query to refresh the document list
    const downloadBtnHandler = () => {
        setView("loading");
        if (downloadList.length > 0) {
            API.post_InvoiceDownload(downloadList)
                .then(response => {
                    if (response.status === 200) {
                        if (response.data.files.poolrechnungen_name !== undefined) {
                            setFileList(prevList => [...prevList, {type: "poolrechnung", file: response.data.files.poolrechnungen, name: response.data.files.poolrechnungen_name}]);
                        }
                        if (response.data.files.abrechnungen_name !== undefined) {
                            setFileList(prevList => [...prevList, {type: "abrechnung", file: response.data.files.abrechnungen, name: response.data.files.abrechnungen_name}]);
                        }
                        if (response.data.files.vorschreibungen_name !== undefined) {
                            setFileList(prevList => [...prevList, {type: "vorschreibung", file: response.data.files.vorschreibungen, name: response.data.files.vorschreibungen_name}]);
                        }
                        setView("download");
                        queryClient.invalidateQueries("GET_ALL_INVOICES_PRIVATE"); // Invalidate the query to refresh the document list
                    }
                })
                .catch(error => {
                    setView("list");
                    console.error("Error downloading files:", error);
                });

            // Reset the download list after downloading
            setDownloadList([]);
        } else {
            alert("Bitte wählen Sie mindestens eine Datei zum Herunterladen aus.");
        }
    }

    // Function to send a support ticket with the question about the invoice
    // It constructs the ticket data based on the selected invoice item and sends it using the API service
    const sendTicket = () => {
        if (questionModalComment.length < 4) {
            return setQuestionCommentError(true);
        }
        setModalView("loading");
        if (modalItem !== null) {
            var itemType = 
                modalItem.item.x_file_poolrechnung_name !== undefined ? "Poolrechnung" :
                modalItem.item.x_file_abrechnung_name !== undefined ? "Abrechnung" :
                modalItem.item.x_file_vorschreibung_name !== undefined ? "Vorschreibung" :
                "Unbekannt";

            var invoice_num = "Kein Rechnungsnummer";

            if (modalItem.item.x_rechnungsnummer && modalItem.item.x_rechnungsnummer !== "False") {
                invoice_num = modalItem.item.x_rechnungsnummer;
            }

            const data = {
                about: `Frage zu ${modalItem.other_type !== null && modalItem.other_type !== "Abrechnung2" ? modalItem.other_type : "Rechnung"}`,
                invoice_id: modalItem.item.id,
                invoice_type: itemType,
                invoice_num: invoice_num,
                comment: questionModalComment,
            }
            API.post_Ticket(data)
                .then(resp => {
                    if (resp.status === 201) {
                        setModalView("success");
                    } else {
                        setModalView("error");
                    }
                })
                .catch(error => {
                    setModalView("error");
                });
                
        }
    }

    // Function to select or deselect all not downloaded invoices
    // It updates the download list and the checkboxes based on the selection state
    // If the value is true, it selects all not downloaded invoices and adds them to the download list
    // If the value is false, it deselects all not downloaded invoices and removes them from the download list
    // It also checks for additional files (file2, mahnung, storno) and adds them to the download list if they are not downloaded
    // It uses the document.getElementById method to update the checkboxes based on the selection state
    // It also handles the case where the invoice type is determined based on the available file names
    // If the invoice type is not recognized, it sets the itemType to null
    // This function is called when the user clicks the checkbox to select or deselect all not downloaded invoices
    // It iterates through the notDownloadedInvoices array and updates the download list accordingly
    // It also updates the checkboxes for each invoice based on the selection state
    const selectAllNotDownloaded = (value) => {
        if (value) {
            notDownloadedInvoices.forEach(item => {
                let itemType = 
                    item.x_file_poolrechnung_name !== undefined ? "poolrechnung" :
                    item.x_file_abrechnung_name !== undefined ? "abrechnung" :
                    item.x_file_vorschreibung_name !== undefined ? "vorschreibung" :
                    null;

                if (item.x_datum_download === "False") {
                    setDownloadList((prevList) => [...prevList, {type: itemType, id: item.id}]);
                    document.getElementById(`${itemType}:${item.id}`).checked = true;
                }
                if (
                    item.x_file_abrechnung2_name && 
                    item.x_file_abrechnung2_name !== "False" && 
                    item.x_datum_download === "False"
                ) {
                    setDownloadList((prevList) => [...prevList, {type: itemType, id: item.id, option: "file2"}]);
                    document.getElementById(`${itemType}:${item.id}:file2`).checked = true;
                    
                }
                if (
                    item.x_file_mahnung_name && 
                    item.x_file_mahnung_name !== "False" && 
                    item.x_datum_download_mahnung === "False"
                ) {
                    setDownloadList((prevList) => [...prevList, {type: itemType, id: item.id, option: "mahnung"}]);
                    document.getElementById(`${itemType}:${item.id}:mahnung`).checked = true;
                }
                if (
                    item.x_file_stornorechnung_name && 
                    item.x_file_stornorechnung_name !== "False" && 
                    item.x_datum_download_storno === "False"
                ) {
                    setDownloadList((prevList) => [...prevList, {type: itemType, id: item.id, option: "storno"}]);
                    document.getElementById(`${itemType}:${item.id}:storno`).checked = true;
                }
            });
        } else {
            notDownloadedInvoices.forEach(item => {
                let itemType = item.x_file_poolrechnung_name? "poolrechnung": item.x_file_abrechnung_name? "abrechnung": "vorschreibung";
                setDownloadList((prevList) => prevList.filter(pi => pi.type !== itemType || pi.id !== item.id));
                const elements = document.querySelectorAll(`[id^="${itemType}:${item.id}"]`);
                elements.forEach(ele => {
                    if ('checked' in ele) {
                        ele.checked = false;
                    }
                });
            });
        }
    }

    // Render the component based on the current view state
    // If the view is "list", it displays the list of invoices with options to select and download them
    // If the view is "loading", it shows a loading spinner indicating that documents are being prepared
    // If the view is "download", it displays the list of files ready for download with an option to go back to the list view
    // It also includes a modal for asking questions about specific invoices, allowing users to create support tickets
    return (
        <>
            {view === "list" &&
                <Card style={{ borderRadius: 15 }} className="shadow-lg">
                    <Card.Body style={{paddingLeft: 0, paddingRight: 0}}>
                        <Row style={{paddingLeft: 15, paddingRight: 15, fontWeight: 600, marginBottom: 10}}>
                            <Col xs="1" md="1" xl="1" className="text-start">
                                <FormCheck
                                    hidden={notDownloadedInvoices.length === 0}
                                    type="checkbox"
                                    onChange={e => selectAllNotDownloaded(e.target.checked)}
                                />
                            </Col>
                            <Col xs="2" md="1" xl="1">Von</Col>
                            <Col xs="9" md="4" xl="4">Rechnungsart</Col>
                            <Col xs="6" md="2" xl="2">Datum</Col>
                            <Col xs="6" md="2" xl="2" className="text-center">Aktionen</Col>
                            <Col xs="12" md="2" xl="2" className="text-center">
                                {downloadList.length > 0 ?
                                    <Button onClick={() => downloadBtnHandler()} className="btn-energo btn-sm">Download ({downloadList.length})</Button> :
                                    "Downloaded" 
                                }
                            </Col>
                        </Row>
                        
                        <ListGroup variant="flush">
                            {notDownloadedInvoices && notDownloadedInvoices.length > 0 &&
                                notDownloadedInvoices.map(inv => (
                                    <PrivateDocDownloadListItem 
                                        invoice={inv} 
                                        key={inv.id} 
                                        question={openQuestionModal}
                                        downlaodListHandler={downlaodListHandler}
                                        preview={openPreview}
                                    />
                                ))
                            }
                            {oldInvoices && oldInvoices.length > 0 &&
                                oldInvoices.map(inv => (
                                    <PrivateDocDownloadListItem 
                                        invoice={inv} 
                                        key={inv.id} 
                                        downloaded 
                                        question={openQuestionModal} 
                                        downlaodListHandler={downlaodListHandler}
                                        preview={openPreview}
                                    />
                                ))
                            }
                            {notDownloadedInvoices.length === 0 && oldInvoices.length === 0 && <ListGroupItem>Keine Dokumente</ListGroupItem>}
                        </ListGroup>
                    </Card.Body>
                </Card>
            }
            {view === "loading" && 
                <Card style={{ borderRadius: 15 }} className="shadow-lg">
                    <Card.Body>
                        <Row className="mt-3">
                            <Col xl="12" className="text-center">
                                <h5>Dokumente werden vorbereitet</h5>
                                <p><Spinner size="30" animation="border" className="text-energo" /></p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            }
            {view === "download" &&
                <Card style={{ borderRadius: 15 }} className="shadow-lg">
                    <Card.Body>
                        <Row className="mb-3">
                            <Col xl="6"><h5>Download ist bereit</h5></Col>
                            <Col xl="6" className="text-end">
                                <Button className="btn-sm" variant="warning" onClick={() => {setView("list"); setFileList([])}}>Zurück zur Liste</Button>
                            </Col>
                        </Row>
                        {fileList && fileList.length > 0 &&
                            <ListGroup variant="flush">
                                {fileList.map((file) => (
                                    <ListGroupItem action key={file.name} onClick={() => downloadZip(file)} style={{cursor: "pointer"}}>
                                        <FaRegFileZipper size={20} className="text-energo" /> {file.name}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        }
                    </Card.Body>
                </Card>
            }
            <Modal show={questionModal} onHide={() => setQuestionModal(false)}>
                <ModalHeader closeButton>
                    <ModalTitle>Support-Ticket erstellen</ModalTitle>
                </ModalHeader>
                <ModalBody>
                    {modalView === "form" &&
                        <>
                            <p>Haben Sie Fragen zu dieser {modalItem?.other_type !== null && modalItem?.other_type !== "Abrechnung2" ? modalItem?.other_type : "Rechnung"}?</p>
                            {modalItem &&
                                <table className="table mt-3">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Von:</td>
                                            <td>
                                                {modalItem.item.x_file_poolrechnung_name && "ENERGO"}
                                                {modalItem.item.x_file_abrechnung_name && "Energielieferant"}
                                                {modalItem.item.x_file_vorschreibung_name && "Energielieferant (Vorschreibung)"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Rechnungsnummer:</td>
                                            <td>
                                                {modalItem.item.x_rechnungsnummer? modalItem.item.x_rechnungsnummer : modalItem.item.id}
                                            </td>
                                        </tr>
                                        {modalItem.other_type !== null && modalItem.other_type !== "Abrechnung2" &&
                                            <tr>
                                                <td className="text-muted">Rechnungsart:</td>
                                                <td>{modalItem.other_type}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            }
                            <FloatingLabel label="Frage">
                                <FormControl 
                                    value={questionModalComment} 
                                    onChange={e => {setQuestionModalComment(e.target.value); setQuestionCommentError(false)}} 
                                    style={{height: 200}} 
                                    as="textarea" 
                                    placeholder="Frage" 
                                    isValid={questionModalComment.length > 3}
                                    isInvalid={questionCommentError}
                                />
                            </FloatingLabel>
                        </>
                    }
                    {modalView === "loading" &&
                        <div className="text-center">
                            <p>Support-Ticket wird erstellt...</p>
                            <Spinner size="30" animation="border" className="text-energo" />
                        </div>
                    }
                    {modalView === "success" &&
                        <div className="text-center">
                            <FaCheckCircle className="text-energo mb-4" size={40} />
                            <p>Support-Ticket erfolgreich erstellt!</p>
                            <p>Wir werden uns so schnell wie möglich bei Ihnen melden.</p>
                        </div>
                    }
                    {modalView === "error" &&
                        <div className="text-center">
                            <FaTimesCircle className="text-danger mb-4" size={40} />
                            <p>Fehler beim Erstellen des Support-Tickets.</p>
                            <p>Bitte versuchen Sie es später erneut.</p>
                        </div>
                    }
                </ModalBody>
                <ModalFooter hidden={modalView === "loading"}>
                    <Button onClick={() => setQuestionModal(false)} variant={modalView === "form"? "warning":"energo"}>{modalView === "form"? "Abbrechen":"Schließen"}</Button>
                    <Button hidden={modalView !== "form"} className="btn-energo" onClick={() => sendTicket()}>Absenden</Button>
                </ModalFooter>
            </Modal>
            <PdfModal show={pdfModal} setShow={setPdfModal} file={pdfPreviewFile} />
        </>
    );
}

export default PrivateDocDownloadList;